import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ChapterDto } from "@mmm/contracts";
import type { SubjectWithChildren } from "../../types.js";
import { downloadStudyNotes } from "../../api/files.js";
import { ExportPreview } from "../organisms/ExportPreview.js";
import { StudyNotesPreview } from "../organisms/StudyNotesPreview.js";
import { GenerateTestDialog } from "../organisms/GenerateTestDialog.js";
import { Button, Checkbox, Icon, useT } from "@mmm/ui";
/** Scope actions shared by subject and chapter workspaces. Test documents
 * own their actions; their parent workspace must not also export notes. */
export function StudyWorkspaceActions({ subject, chapter, tests = false }: {
  subject: SubjectWithChildren;
  chapter?: ChapterDto;
  tests?: boolean;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [includeChapters, setIncludeChapters] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const testsPath = chapter
    ? `/subjects/${subject.id}/chapters/${chapter.id}/tests`
    : `/subjects/${subject.id}/tests`;
  const scope = { subjectId: subject.id, chapterId: chapter?.id, includeChapters };

  return (
    <>
      <div className="mmm-workspace-actions">
        {!tests && (
          <>
            <ExportPreview
              options={!chapter && <Checkbox label={t("subjects.includeChapters")} checked={includeChapters}
                onChange={(event) => setIncludeChapters(event.target.checked)} />}
              preview={<StudyNotesPreview {...scope} />}
              onWord={() => downloadStudyNotes({ ...scope, format: "docx" })}
              onPdf={() => downloadStudyNotes({ ...scope, format: "pdf" })}
            />
            <Button onClick={() => navigate(testsPath)}>
              <Icon name="doc" />{t("subjects.viewTests")}
            </Button>
          </>
        )}
        <Button variant="primary" onClick={() => setShowGenerate(true)}>
          <Icon name="ai" />{t("tests.generateTest")}
        </Button>
      </div>
      {showGenerate && <GenerateTestDialog subject={subject} lockedChapter={chapter}
        onClose={() => setShowGenerate(false)}
        onCreated={(test) => {
          setShowGenerate(false);
          navigate(`/subjects/${subject.id}/tests/${test.id}`);
        }} />}
    </>
  );
}
